import * as React from 'react';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import DraftListViewmodel from '../Viewmodels/DraftListViewmodel';
import PermissionCheck, { CheckHasUserType } from '../../Utilities/PermissionCheck';
import { Permission } from '../../Permissions/Permissions';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import { ResponseType, TokenRefreshContext } from '../../Contexts/TokenRefreshContext';
import authService from '../../api-authorization/AuthorizeService';
import CloseDialogConfirmation from '../../Utilities/CloseDialogConfimation';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import DialogActions from '@mui/material/DialogActions';
import DropdownViewModel from '../../Utilities/ViewModels/DropdownViewModel';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { ApprovalStatus } from '../../SignOff/Viewmodels/SignOffViewmodel';
import { AlertContext } from '../../Contexts/AlertContext';

interface IProps {
    assetId: number;
    row: DraftListViewmodel;
    refresh: () => void;
    userId: string;
}

export default function DraftsTableRow(props: IProps) {
    const { assetId, row, refresh, userId } = props;
    const navigate = useNavigate();
    const { crabFetch } = React.useContext(TokenRefreshContext);
    const { show } = React.useContext(AlertContext);
    const [openConfirmation, setOpenConfirmation] = React.useState(false);
    const [openRetractConfirmation, setOpenRetractConfirmation] = React.useState(false);
    const [openSupersedeConfirmation, setOpenSupersedeConfirmation] = React.useState(false);
    const [isAdmin, setIsAdmin] = React.useState(false);
    const [user, setUser] = React.useState<string>('');
    const [users, setUsers] = React.useState<DropdownViewModel[]>([]);

    React.useEffect(() => {
        getIsAdmin();
        getUsers();
    }, []);

    const getUsers = async () => {
        const token = await authService.getAccessToken();
        const docType = row.documentType === "WSE" ? "wse" : "report";

        crabFetch(`User/GetCreateDocumentDropDown?type=${docType}`, {
            headers: !token ? { 'Content-Type': 'application/json; charset=utf-8' } : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json; charset=utf-8' },
        }, ResponseType.JSON,
            (data: DropdownViewModel[]) => {
                setUsers(data);
            },
            () => {
            }
        );
    }

    const discard = async () => {
        const token = await authService.getAccessToken();
        const controller = row.documentType === "WSE" ? "Wse" : "InspectionReport";

        await crabFetch(`${controller}/DiscardDraft?id=${row.id}`, {
            headers: !token ? { 'Content-Type': 'application/json; charset=utf-8' } : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json; charset=utf-8' },
        }, ResponseType.Text,
            (data: any) => {
                if (data.length > 0) {
                    show('error', data);
                }

                refresh();
            },
            () => {
            }
        );
    }

    const retract = async () => {
        const token = await authService.getAccessToken();
        const controller = row.documentType === "WSE" ? "Wse" : "InspectionReport";

        await crabFetch(`${controller}/Retract?id=${row.id}&newAuthor=${user.length > 0 ? user : ''}`, {
            headers: !token ? { 'Content-Type': 'application/json; charset=utf-8' } : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json; charset=utf-8' },
        }, ResponseType.Text,
            (data: any) => {
                if (data.length > 0) {
                    show('error', data);
                }

                refresh();
            },
            () => {
            }
        );
    }

    const supersede = async () => {
        const token = await authService.getAccessToken();
        const controller = row.documentType === "WSE" ? "Wse" : "InspectionReport";

        await crabFetch(`${controller}/Supersede?id=${row.id}`, {
            headers: !token ? { 'Content-Type': 'application/json; charset=utf-8' } : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json; charset=utf-8' },
        }, ResponseType.Text,
            (data: any) => {
                if (data.length > 0) {
                    show('error', data);
                }

                refresh();
            },
            () => {
            }
        );
    }

    const view = () => {
        if (row.documentType === 'WSE') {
            navigate(
                `/Wse/${assetId}/${row.id}`
            );
        } else {
            navigate(
                `/InspectionReport/${assetId}/${row.id}`
            );
        }
    }

    const close = (refresh: boolean) => {
        setOpenConfirmation(false);

        if (refresh) {
            discard();
        }
    }

    const closeSupersede = (refresh: boolean) => {
        setOpenSupersedeConfirmation(false);

        if (refresh) {
            supersede();
        }
    }

    const closeRetract = (refresh: boolean) => {
        setOpenRetractConfirmation(false);
        setUser('');

        if (refresh) {
            retract();
        }
    }

    const getIsAdmin = async () => {
        setIsAdmin(await CheckHasUserType('Administrator'));
    }

    return (
        <TableRow>
            <TableCell>{row.documentType}</TableCell>
            <TableCell>{row.reference}</TableCell>
            <TableCell>{row.status}</TableCell>
            <TableCell>{row.approvalStatus !== null && ApprovalStatus[row.approvalStatus]}</TableCell>
            <TableCell>{row.lastChangedBy}</TableCell>
            <TableCell>{row.owner}</TableCell>
            <TableCell>{row.approver}</TableCell>
            <TableCell align="right">
                {row.status === 'Draft' &&
                    <Grid container spacing={2} justifyContent="flex-end">
                        {isAdmin &&
                            <Grid item>
                                <Button variant="outlined" size="small" onClick={() => setOpenRetractConfirmation(true)}>Reassign</Button>
                            </Grid>
                        }
                        <PermissionCheck permission={Permission.DeleteDrafts}>
                            {row.draftedById === userId &&
                                <Grid item>
                                    <Button variant="outlined" size="small" onClick={() => setOpenConfirmation(true)}>Discard Draft</Button>
                                </Grid>
                            }
                        </PermissionCheck>
                        <PermissionCheck permission={row.documentType === 'WSE' ? [Permission.CreateWse, Permission.SignOffWse, Permission.SignOffEscalatedDocument] : [Permission.CreateReport, Permission.SignOffReport, Permission.SignOffEscalatedDocument]}>
                            {row.draftedById === userId ?
                                <Grid item>
                                    <Button variant="contained" size="small" onClick={view}>Continue</Button>
                                </Grid>
                                :
                                <Grid item>
                                    <Button variant="contained" size="small" onClick={view}>View</Button>
                                </Grid>
                            }
                        </PermissionCheck>
                    </Grid>
                }
                {row.status !== 'Draft' &&
                    <Grid container spacing={2} justifyContent="flex-end">
                        {(row.status === 'Issued' && row.approvalStatus === ApprovalStatus.Rejected) &&
                            <PermissionCheck permission={[Permission.SignOffReport, Permission.SignOffEscalatedDocument]}>
                                <Grid item>
                                    <Button variant="outlined" size="small" onClick={() => setOpenSupersedeConfirmation(true)}>Supersede</Button>
                                </Grid>
                            </PermissionCheck>
                        }
                        {row.canRetract === true &&
                            <Grid item>
                                <Button variant="outlined" size="small" onClick={() => setOpenRetractConfirmation(true)}>Retract</Button>
                            </Grid>
                        }
                        <Grid item>
                            <PermissionCheck permission={[Permission.ViewDocs, row.documentType === 'WSE' ? Permission.SignOffWse : Permission.SignOffReport, row.documentType === 'WSE' ? Permission.CreateWse : Permission.CreateReport, Permission.AcknowledgeDocument, Permission.SignOffEscalatedDocument]}>
                                <Button variant="contained" size="small" onClick={view}>View</Button>
                            </PermissionCheck>
                        </Grid>
                    </Grid>
                }
            </TableCell>
            <CloseDialogConfirmation confirmClosureMessage={'Yes'} preventClosureMessage={'No'} title={'Are you sure you want to discard ' + row.reference + ' ?'} open={openConfirmation} onClose={close}></CloseDialogConfirmation>
            <CloseDialogConfirmation confirmClosureMessage={'Yes'} preventClosureMessage={'No'} title={'Are you sure you want to supersede ' + row.reference + ' ?'} open={openSupersedeConfirmation} onClose={closeSupersede}></CloseDialogConfirmation>
            <Dialog
                open={openRetractConfirmation}
                onClose={() => closeRetract(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogContent>
                    <Grid container>
                        {row.status !== "Draft" &&
                            <Grid item xs={12}>
                                <Typography>{'Are you sure you want to retract ' + row.reference + ' ?'}</Typography>
                            </Grid>
                        }
                        {isAdmin &&
                            <>
                                <Grid item xs={12}>
                                    <Typography>{'Assign new author?'}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField variant="outlined" size="small" fullWidth select value={user} onChange={(e) => setUser(e.target.value)}>
                                        {users.map(item =>
                                            <MenuItem key={item.id} value={item.id}>{item.label}</MenuItem>
                                        )}
                                    </TextField>
                                </Grid>
                            </>
                        }
                    </Grid>
                    <DialogActions>
                        <Button onClick={() => closeRetract(true)}>{'Yes'}</Button>
                        <Button onClick={() => closeRetract(false)} variant="contained">{'No'}</Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </TableRow>
    );
}